import { useMemo } from "react";
import { Link } from "react-router-dom";
import { usePageMeta, useJsonLd, breadcrumbJsonLd } from "../lib/seo";
import { CONTACT_EMAIL, GOVERNING_LAW, LEGAL_LAST_UPDATED, OPERATOR_ENTITY } from "../lib/legal";

export function TermsPage() {
  usePageMeta({
    title: "Terms of Service",
    description:
      "The rules for using MarketOnBase: fees, the nonrefundable platform fee, how disputes are resolved, what may not be sold, and the limits of what the platform is responsible for.",
    path: "/terms",
  });

  useJsonLd(
    useMemo(
      () =>
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Terms of Service", path: "/terms" },
        ]),
      [],
    ),
  );

  return (
    <article className="stack" style={{ maxWidth: 720 }}>
      <div>
        <h1>Terms of Service</h1>
        <p className="muted">Last updated: {LEGAL_LAST_UPDATED}</p>
      </div>

      <div className="alert alert--warning" role="note">
        <strong>ToS is active and applies across MarketOnBase.com</strong>
        <p>
          08/01/2026
        </p>
      </div>

      <section className="stack">
        <h2>1. What this service is</h2>
        <p>
          MarketOnBase is a marketplace where sellers list digital goods and buyers pay for them
          using USDC held in a non-custodial smart contract on the Base blockchain. By using
          the service you agree to these terms. If you don&apos;t agree, don&apos;t use it.
        </p>
        <p>
          We are a venue and a piece of software. We are not a party to the contract between a
          buyer and a seller, we do not take custody of your funds, and we do not verify the
          quality, legality, or fitness of anything listed.
        </p>
      </section>

      <section className="stack">
        <h2>2. Eligibility</h2>
        <p>To use this service you confirm that you:</p>
        <ul>
          <li>are at least 18 years old and able to enter a binding contract;</li>
          <li>
            are not located in, or acting on behalf of anyone in, a jurisdiction subject to
            comprehensive sanctions, and are not on any applicable sanctions or restricted-party
            list;
          </li>
          <li>
            will not use the service where doing so would break the law that applies to you.
          </li>
        </ul>
      </section>

      <section className="stack">
        <h2>3. Your account and wallet</h2>
        <p>
          Signing in creates a Coinbase-managed Smart Account on Base that belongs to you. We never
          hold your private keys and cannot sign transactions for you, recover your account if you
          lose access to the email behind it, or reverse a transaction you have signed.
        </p>
        <p>
          You are responsible for everything done through your account, and for keeping access to
          your sign-in email secure.
        </p>
        <p>
          Your public display name must not impersonate another person, business, or this platform
          or its staff. We may reclaim or reset a name that does.
        </p>
      </section>

      <section className="stack">
        <h2>4. Fees</h2>
        <ul>
          <li>
            Buyers pay the seller&apos;s listed price (the &ldquo;principal&rdquo;) plus a{" "}
            <strong>10% platform fee</strong>, itemized at checkout before anything is signed.
          </li>
          <li>Sellers receive the full principal; the fee is added on top, not deducted.</li>
          <li>
            <strong>The platform fee is nonrefundable.</strong> It is charged and paid to the
            treasury when the funding transaction confirms onchain, and it is not returned even if
            the principal is later refunded to the buyer in whole or in part through a dispute or a
            seller-side cancellation. Only the principal can be released, refunded, or split.
          </li>
          <li>
            You are separately responsible for blockchain network fees and for any taxes arising
            from your transactions.
          </li>
        </ul>
      </section>

      <section className="stack">
        <h2>5. How a transaction settles</h2>
        <p>
          When a buyer funds an order, the principal is held by the smart contract. It can
          then move in exactly these ways:
        </p>
        <ul>
          <li>the buyer releases it to the seller;</li>
          <li>
            the dispute window passes without a release or a dispute, after which the seller may
            claim it;
          </li>
          <li>
            a dispute is opened before the deadline and an arbitrator resolves it, splitting the
            principal between buyer and seller — possibly entirely to one side.
          </li>
        </ul>
        <p>
          The contract requires that the buyer&apos;s and seller&apos;s shares in a resolution add
          up to exactly the principal, so funds cannot be diverted elsewhere, including to the
          arbitrator or to us.
        </p>
        <p>
          <strong>Transactions are irreversible.</strong> There is no chargeback mechanism. Once a
          settlement is confirmed onchain it is final.
        </p>
      </section>

      <section className="stack">
        <h2>6. Disputes</h2>
        <p>
          A buyer may open a dispute at any point before the dispute deadline shown on their order.
          Opening a dispute pauses release. Both sides may submit written statements and evidence.
        </p>
        <p>
          An arbitrator then reviews the submissions and issues a resolution onchain.{" "}
          <strong>Arbitration decisions are final</strong> as far as this platform is concerned;
          the resolution is executed by the smart contract and cannot be undone afterwards.
        </p>
        <p>
          If no dispute is opened before the deadline, the buyer loses the ability to dispute that
          order through this service.
        </p>
        <p>
          Submitting knowingly false statements or fabricated evidence in a dispute is grounds for
          suspension.
        </p>
      </section>

      <section className="stack">
        <h2>7. Rules for sellers</h2>
        <p>By listing an item, you represent that:</p>
        <ul>
          <li>you have the right to sell it and to grant buyers the rights you claim to grant;</li>
          <li>the listing accurately describes what a buyer will receive;</li>
          <li>the file does not contain malware or anything designed to harm a buyer.</li>
        </ul>
        <p>You may not list:</p>
        <ul>
          <li>anything illegal to sell or possess where you or the buyer are;</li>
          <li>
            content that infringes someone else&apos;s copyright, trademark, or other rights,
            including resold material you don&apos;t hold rights to;
          </li>
          <li>
            malware, exploits, stolen data, credentials, or account access; personal data of other
            people;
          </li>
          <li>sexual content involving minors, or any content depicting real non-consensual acts;</li>
          <li>anything whose sale would make this platform a party to fraud.</li>
        </ul>
        <p>
          Buyers receive the exact file version attached to their order. Replacing the file on a
          listing later does not change what past buyers are entitled to.
        </p>
      </section>

      <section className="stack">
        <h2>8. Rules for buyers</h2>
        <ul>
          <li>
            Confirm the itemized total, the seller&apos;s address, and the dispute window before
            signing. You are signing an irreversible transaction.
          </li>
          <li>
            Download and check what you bought promptly, while the dispute window is still open.
          </li>
          <li>
            Don&apos;t redistribute what you buy beyond the rights the seller granted you.
          </li>
          <li>
            Don&apos;t open disputes in bad faith after receiving and keeping what you paid for.
          </li>
        </ul>
      </section>

      <section className="stack">
        <h2>9. File scanning</h2>
        <p>
          Uploaded files are scanned for malware before a listing can be published, and files that
          fail are rejected. This meaningfully reduces risk but is <strong>not a guarantee</strong>
          — no scanner detects everything. Scan results are not a warranty about a file&apos;s
          safety, quality, or contents.
        </p>
      </section>

      <section className="stack">
        <h2>10. Suspension and removal</h2>
        <p>
          We may remove a listing, suspend an account, or refuse service where we reasonably
          believe these terms have been broken, where required by law, or to protect users from
          fraud or harm.
        </p>
        <p>
          Suspension stops new activity — a suspended seller&apos;s listings stop being purchasable
          — but it does not and cannot seize held funds. Orders already funded continue to
          settle through the contract&apos;s normal paths, including dispute resolution.
        </p>
      </section>

      <section className="stack">
        <h2>11. No warranty</h2>
        <p>
          The service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo;, without
          warranties of any kind, whether express or implied, including any implied warranties of
          merchantability, fitness for a particular purpose, or non-infringement.
        </p>
        <p>
          We do not warrant that the service will be uninterrupted or error-free, that listings are
          accurate or lawful, that files are safe or of any particular quality, or that the
          blockchain network will confirm any given transaction.
        </p>
        <p>
          The smart contract has been internally reviewed and tested but has not received an
          independent third-party audit. Smart contracts can contain undiscovered defects. You use
          this service with that risk in mind.
        </p>
      </section>

      <section className="stack">
        <h2>12. Limitation of liability</h2>
        <p>
          To the maximum extent permitted by law, {OPERATOR_ENTITY} is not liable for indirect,
          incidental, special, consequential, or punitive damages, or for lost profits, lost data,
          or lost cryptocurrency, arising from your use of the service.
        </p>
        <p>
          To the maximum extent permitted by law, total aggregate liability for any claim relating
          to the service is limited to the platform fees you paid to us in connection with the
          transaction giving rise to the claim.
        </p>
        <p>
          Nothing here excludes liability that cannot lawfully be excluded, including for fraud.
          Some jurisdictions do not allow certain exclusions, in which case those exclusions do not
          apply to you.
        </p>
      </section>

      <section className="stack">
        <h2>13. Indemnity</h2>
        <p>
          You agree to indemnify {OPERATOR_ENTITY} against claims, losses, and reasonable legal
          costs arising from your listings, your use of the service, or your breach of these terms
          or of anyone else&apos;s rights.
        </p>
      </section>

      <section className="stack">
        <h2>14. Changes</h2>
        <p>
          We may update these terms as the service changes. Material changes will be reflected in
          the &ldquo;last updated&rdquo; date above. Continuing to use the service after a change
          means you accept the updated terms. Terms governing an order are those in effect when
          that order was funded.
        </p>
      </section>

      <section className="stack">
        <h2>15. Governing law</h2>
        <p>
          These terms are governed by the laws of {GOVERNING_LAW}, without regard to conflict of
          law rules. This provision is a placeholder pending legal review and must be settled
          before commercial operation.
        </p>
      </section>

      <section className="stack">
        <h2>16. Contact</h2>
        <p>
          Questions about these terms can be sent to{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
        <p className="text-sm muted">
          See also our <Link to="/privacy">Privacy Policy</Link> and{" "}
          <Link to="/how-it-works">how the process works</Link>.
        </p>
      </section>
    </article>
  );
}
